import React, { useState } from 'react'

import { Button, Icon, Popover } from '@clientbase/clientbase-library'
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { usePermission } from 'hooks/usePermission'
import moment from 'moment'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import { formatCouponPercent } from '../../utils/formatCouponPercent'

interface CouponUsageData {
  couponCode?: string
  discountType?: string
  discountValue?: string | number
  appliedDiscountAmount?: string | number
  createdAt?: string
}

interface CouponValueDisplayProps {
  originalAmount: number
  finalAmount: number
  couponUsage: CouponUsageData
  onRemoveClick: () => void
  canRemove: boolean
  perCycleLabel?: string
}

const CouponValueDisplay: React.FC<CouponValueDisplayProps> = ({
  originalAmount,
  finalAmount,
  couponUsage,
  onRemoveClick,
  canRemove,
  perCycleLabel
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  // Desvincular chama billings#remove_coupon / recurrences#remove_coupon, que
  // exigem coupons:manage. Sem ela o botão só levava a um 403.
  const canManageCoupons = usePermission('coupons:manage')

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handleRemove = () => {
    setAnchorEl(null)
    onRemoveClick()
  }

  const discountLabel =
    couponUsage.discountType === 'percentage'
      ? `${formatCouponPercent(couponUsage.discountValue)} de desconto`
      : `${formatMoney(couponUsage.discountValue)} de desconto`

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography
          variant="smLight"
          sx={{
            textDecoration: 'line-through',
            color: theme.palette.neutral[400]
          }}
        >
          {formatMoney(originalAmount)}
        </Typography>
        <Typography variant="smBold">{formatMoney(finalAmount)}</Typography>
        <Tooltip title="Detalhes do cupom" arrow placement="top">
          <IconButton
            onClick={handleOpen}
            aria-label="Detalhes do cupom"
            sx={{ padding: '6px', my: '-6px' }}
          >
            <Icon icon="faTicket" sx={{ fontSize: '16px' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            padding: '16px',
            width: '300px',
            borderRadius: '8px'
          }
        }}
      >
        <Stack gap={1.25}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Icon
              icon="faTicket"
              sx={{ fontSize: '18px', color: theme.palette.primary[200] }}
            />
            <Typography variant="smBold">{couponUsage.couponCode}</Typography>
          </Stack>

          <Typography variant="smLight" color={theme.palette.neutral[400]}>
            {discountLabel}
          </Typography>

          <Divider />

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Valor original
            </Typography>
            <Typography variant="smLight">
              {formatMoney(originalAmount)}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Desconto aplicado{perCycleLabel ? ` (${perCycleLabel})` : ''}
            </Typography>
            <Typography variant="smLight" color={theme.palette.primary[200]}>
              -{formatMoney(couponUsage.appliedDiscountAmount)}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="smBold">Valor final</Typography>
            <Typography variant="smBold">{formatMoney(finalAmount)}</Typography>
          </Stack>

          {couponUsage.createdAt && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  Aplicado em
                </Typography>
                <Typography variant="smLight">
                  {moment(couponUsage.createdAt).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </Stack>
            </>
          )}

          {canRemove && canManageCoupons && (
            <>
              <Divider />
              <Button
                variantButton="action"
                onClick={handleRemove}
                startIcon="close"
                fullWidth
              >
                Desvincular cupom
              </Button>
            </>
          )}
        </Stack>
      </Popover>
    </>
  )
}

export default CouponValueDisplay
